import { parsePaymentsValidationError } from '@/api'
import {
  PAYPAL_CONSENT_CANCELLED_STATE,
  type RequestPayoutVars,
  clearStash,
  peekFreshStash,
  readPayPalCallbackParams,
  useRequestPayout
} from '@/features/wallet/api'
import { routes } from '@/pages/routes'
import { Button, Text } from '@/ui-kit'
import { Trans, useLingui } from '@lingui/react/macro'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

type PayPalAuthPageState = 'submitting' | 'terminal' | 'retryable'

/**
 * PayPal consent callback page. The authorization code never appears here in the URL —
 * `capturePayPalCallbackParams()` already copied it into module memory and stripped the query
 * at boot, before PostHog/Sentry init (IV3). This page verifies the CSRF state nonce against
 * the ceremony stash (IV1), then auto-submits the payout under the stash's Idempotency-Key so
 * any double-fire replays instead of double-debiting (IV2).
 */
export const PayPalAuthPage = () => {
  const navigate = useNavigate()
  const { t } = useLingui()
  const { mutateAsync } = useRequestPayout()
  const [state, setState] = useState<PayPalAuthPageState>('submitting')
  // StrictMode mounts effects twice — this ref keeps the ceremony from being evaluated (and the
  // POST from firing) twice. The stash idempotency key is the real double-debit guard (IV2);
  // the ref is a UX nicety.
  const startedRef = useRef(false)
  // Kept across renders so "Try again" re-fires the exact same key+body (safe replay).
  const submitVarsRef = useRef<RequestPayoutVars | null>(null)

  const submit = useCallback(async () => {
    const vars = submitVarsRef.current
    if (!vars) return
    setState('submitting')
    try {
      // 2xx and the hook's silent 409 (replay) path both resolve here.
      await mutateAsync(vars)
      clearStash()
      toast.success(t`Payout requested`)
      navigate(routes.wallet.path)
    } catch (err) {
      if (parsePaymentsValidationError(err)) {
        // VALIDATION — the body is permanently rejected; retrying the same body is pointless.
        clearStash()
        setState('terminal')
      } else {
        // Transport/5xx — same key + same body is a safe replay, so keep the stash and offer retry.
        setState('retryable')
      }
    }
  }, [mutateAsync, navigate, t])

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const params = readPayPalCallbackParams()

    // User cancelled the consent screen — keep the stash so the modal can resume (IV5).
    if (params?.error) {
      navigate(routes.wallet.path, { state: { [PAYPAL_CONSENT_CANCELLED_STATE]: true } })
      return
    }

    const stash = peekFreshStash()
    const code = params?.code

    // No code and no fresh stash: nothing to do (covers a post-success refresh of this page).
    if (!code && !stash) {
      navigate(routes.wallet.path)
      return
    }

    // Code without a fresh stash (or a leftover stash without a code) — the ceremony can't be
    // completed safely. Start over.
    if (!code || !stash) {
      clearStash()
      setState('terminal')
      return
    }

    // IV1: the CSRF state must match the stash nonce before any submit fires.
    if (params?.state !== stash.stateNonce) {
      clearStash()
      setState('terminal')
      return
    }

    submitVarsRef.current = {
      method: 'paypal',
      amountUsd: stash.amountUsd,
      methodInput: { authCode: code, currency: 'USD' },
      idempotencyKey: stash.idempotencyKey
    }
    void submit()
    // startedRef makes re-runs no-ops, so identity changes of navigate/submit are harmless.
  }, [navigate, submit])

  const handleBackToWallet = useCallback(() => {
    navigate(routes.wallet.path)
  }, [navigate])

  const handleTryAgain = useCallback(() => {
    void submit()
  }, [submit])

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4">
      {state === 'submitting' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            <Trans>Completing your withdrawal…</Trans>
          </Text>
          <Text className="colorWhiteToBlack my-2 text-center">
            <Trans>Please wait — don&apos;t close this tab.</Trans>
          </Text>
        </>
      )}

      {state === 'terminal' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            <Trans>This PayPal session expired — start the withdrawal over.</Trans>
          </Text>
          <Button className="mt-4" onClick={handleBackToWallet}>
            <span className="colorWhite">
              <Trans>Back to wallet</Trans>
            </span>
          </Button>
        </>
      )}

      {state === 'retryable' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            <Trans>Couldn&apos;t complete your withdrawal.</Trans>
          </Text>
          <Button className="mt-4" onClick={handleTryAgain}>
            <span className="colorWhite">
              <Trans>Try again</Trans>
            </span>
          </Button>
          <Button className="mt-2" variant="tertiary" onClick={handleBackToWallet}>
            <Trans>Back to wallet</Trans>
          </Button>
        </>
      )}
    </div>
  )
}

export default PayPalAuthPage
